"use client";

import { useState } from "react";
import { Link } from "@/i18n/navigation";
import CompareSlider from "@/app/[locale]/ai-hair-color-changer/CompareSlider";
import { DEMO_BASE_IMAGE, DEMO_SHADES } from "@/app/[locale]/ai-hair-color-changer/demoShades";
import { hairFinderUrl } from "@/lib/hairFinderHandoff";
import { trackGizmo } from "@/lib/analytics";
import styles from "./hairColorDemo.module.css";

type DemoShade = (typeof DEMO_SHADES)[number];

export default function HairColorDemo({ onTryOnPhoto, source = "hair-tool", external = false }: {
  onTryOnPhoto?: (shade: DemoShade) => void;
  source?: string;
  external?: boolean;
}) {
  const [shade, setShade] = useState<DemoShade>(DEMO_SHADES[2]);
  return <section className={styles.demo} aria-label="Interactive hair color example">
    <div className={styles.heading}><strong>One photo. Six color directions.</strong><span>Interactive example</span></div>
    <CompareSlider beforeSrc={DEMO_BASE_IMAGE} afterSrc={shade.src}
      beforeAlt="Example model with brown hair before the color preview"
      afterAlt={`The same example model with ${shade.name} hair`}
      className={styles.comparison} />
    <p className={styles.hint}>Drag to compare · <strong aria-live="polite">{shade.name}</strong></p>
    <div className={styles.shades} role="group" aria-label="Example hair shades">
      {DEMO_SHADES.map(option => <button key={option.id} type="button" aria-pressed={shade.id === option.id}
        onClick={() => { setShade(option); if (!external) trackGizmo("hair_demo_try", { shade: option.id, source, mode: "demo" }); }}>
        <span style={{ background: option.hex }} aria-hidden="true" />{option.name}
      </button>)}
    </div>
    <p className={styles.caption}>AI-generated examples on a model. A preview helps compare colors; it cannot predict a dye formula or salon result.</p>
    {onTryOnPhoto
      ? <button type="button" className={styles.action} onClick={() => onTryOnPhoto(shade)}>Try {shade.name} on my photo →</button>
      : external ? <a className={styles.action} href={`https://www.coloranalysistech.com${hairFinderUrl([shade.id])}&utm_source=partner_demo&utm_medium=referral`} target="_blank" rel="noopener noreferrer">Try this shade on my photo →</a>
      : <Link className={styles.action} href={hairFinderUrl([shade.id])}
          onClick={() => trackGizmo("hair_demo_next", { source, shade: shade.id, mode: "demo" })}>Try this shade on my photo →</Link>}
  </section>;
}
